import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  ApolloLink,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { fetchSessionToken } from "../lib/app-bridge.js";

const httpLink = createHttpLink({
  uri: (import.meta.env.VITE_GRAPHQL_URL as string | undefined) ?? "/graphql",
});

/**
 * Attach a fresh Shopify App Bridge session token to every request.
 *
 * App Bridge JWTs expire after ~60 s, so we re-fetch on every operation
 * rather than caching — getSessionToken() is cheap (reads from the frame).
 *
 * In local dev (no App Bridge) falls back to sessionStorage so you can
 * manually paste a token for testing:
 *   sessionStorage.setItem("shopify-session-token", "<jwt>")
 */
const authLink = new ApolloLink((operation, forward) => {
  return new Observable((observer) => {
    fetchSessionToken()
      .then((bridgeToken) => {
        const token =
          bridgeToken ?? sessionStorage.getItem("shopify-session-token");

        if (token) {
          operation.setContext({
            headers: { Authorization: `Bearer ${token}` },
          });
        }

        forward(operation).subscribe({
          next: (v) => observer.next(v),
          error: (e) => observer.error(e),
          complete: () => observer.complete(),
        });
      })
      .catch(() => {
        forward(operation).subscribe({
          next: (v) => observer.next(v),
          error: (e) => observer.error(e),
          complete: () => observer.complete(),
        });
      });
  });
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) => {
      console.error(`[GraphQL error]: ${message}`, { locations, path });
    });
  }
  if (networkError) {
    console.error("[Network error]:", networkError);
  }
});

export const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      SubscriptionOrder: {
        fields: {
          shopifyOrderIds: { merge: false },
        },
      },
      Dispenser: {
        fields: {
          productOptions: { merge: false },
          categories: { merge: false },
        },
      },
      ProductLineConfig: {
        fields: {
          certifications: { merge: false },
        },
      },
    },
  }),
  defaultOptions: {
    watchQuery: { errorPolicy: "all" },
    query: { errorPolicy: "all" },
  },
});
